/**
 * The "last visited page" — one module owns the storage key, the writes, and
 * the validation on read.
 *
 * WHY THIS MODULE EXISTS (board 5KO2U27jcbmmD8S2emNH, 2026-09-10)
 * ---------------------------------------------------------------
 * The literal `'ask-jds-last-visited-page'` was defined in three files that did
 * not import from each other: `AuthPage.tsx`, `GroupInviteLanding.tsx`, and
 * `use-last-visited-page.ts`. Two of them WROTE it and one READ it as a
 * post-login navigation target — with no validation anywhere, and with the read
 * site falling back to a hardcoded `/chat` that bypassed
 * `getPostLoginRedirect()` entirely. An ad visitor who signed up for the
 * NextGen trial landed on `/chat` and never started the trial they clicked for.
 *
 * There is deliberately NO React in this file: `src/lib/auth-utils.ts` imports
 * it, and `getPostLoginRedirect()` is called from plain functions and from
 * effects alike. A hook could not serve both.
 *
 * THE VALUE IS KEYED BY USER ID (board sYIAm69jLziJCcJifsMO, 2026-09-10)
 * ----------------------------------------------------------------------
 * Storage is per-BROWSER; "where I left off" is per-USER. For the ~30 minutes
 * between founder decision A shipping and this fix, those two were conflated:
 * the value was a bare path with no identity attached, and it was never cleared
 * on sign out. The founder signed up a BRAND-NEW account on a browser that
 * still held a previous test account's `/nextgen/skills/practice`, and the new
 * account — which by definition has no history of its own — inherited it. The
 * ONLY way this key can be populated at signup time is a DIFFERENT account
 * having used the same browser, so every hit on that path is either wrong or a
 * small privacy leak on a shared computer (person B lands on person A's last
 * page).
 *
 * The stored value is therefore `{ uid, path }`, and a read returns the path
 * ONLY when the stored uid matches the caller's. A mismatch is CLEARED, which
 * is what actually removes the leak rather than merely declining to act on it.
 *
 * THE UID MUST BE THE SAME IDENTIFIER ON BOTH SIDES — this is the one way this
 * fix can fail silently. `AuthUser.id` is assigned `firebaseUser.uid` in
 * `toAuthUser()` (`src/lib/auth.tsx`), so `useAuth().user.id` and
 * `firebaseAuth.currentUser.uid` are the SAME string and either may be passed.
 * Anything else — a profile id, a Postgres row id, or an `undefined` from a
 * caller that forgot the argument — makes every comparison miss, and every
 * login silently reverts to `/dashboard` with nothing erroring
 * (`.claude/rules/silent-degradation.md`: a comparison that can never match).
 * `src/lib/__tests__/last-visited-page.test.ts` pins that identifier's source.
 *
 * VALIDATION HAPPENS ON READ, NOT ONLY ON WRITE
 * ----------------------------------------------
 * `useLastVisitedPage` filters `/auth` and `/login` at write time, but
 * localStorage is attacker-writable in any XSS scenario. Since the value
 * becomes a NAVIGATION TARGET, the read is the only place a guard is
 * load-bearing. Anything that fails validation is cleared, not merely ignored,
 * so a bad value cannot sit in storage being re-rejected on every login.
 *
 * Every storage access is wrapped: `localStorage` THROWS (it does not return
 * null) in Safari private mode and when a browser is set to block site data.
 * The same reason `src/lib/auth-intent.ts` wraps its own access. A storage
 * failure must degrade to the domain default, never to a crashed auth form.
 */

export const LAST_VISITED_PAGE_KEY = 'ask-jds-last-visited-page';

/**
 * C0 controls, DEL, and every form of whitespace.
 *
 * This is a SECURITY check, not tidiness. Browsers STRIP tab, LF and CR from a
 * URL before parsing it, so the prefix tests below run against a different
 * string than the one that is actually navigated: `/\t/evil.com` starts with
 * neither `//` nor `/\`, passes every prefix test, and then resolves to
 * `https://evil.com/`. Verified by execution rather than by reading — `new
 * URL('/\t/evil.com', 'https://jdsimplified.com').href` is `https://evil.com/`,
 * while `/outlines` and `/outlines?tab=1` are unaffected.
 *
 * Rejecting rather than stripping is deliberate: a legitimate value here comes
 * from `location.pathname + location.search`, which the browser has already
 * percent-encoded, so a raw control character or space cannot occur in one.
 * `\s` is included alongside the explicit C0 range to cover NBSP and the
 * Unicode line separators.
 */
const CONTROL_OR_WHITESPACE = /[\u0000-\u0020\u007F\s]/;

/**
 * A value is usable as a post-login destination only if it is unambiguously a
 * same-origin path.
 *
 * The `//` and `/\` cases are the same defect wearing two hats: browsers treat
 * BOTH as protocol-relative, so `//evil.com` and `/\evil.com` navigate
 * off-origin while still "starting with a slash". Rejecting only `//` would
 * leave the backslash form as a working open redirect.
 *
 * `/auth` and `/login` are excluded because sending a freshly-authenticated
 * user back to the auth page is the redirect loop that `AuthPage`'s
 * `auth_redirect_attempts` counter exists to break.
 *
 * This matters most at the two HARD sinks, where the value is assigned to
 * `window.location.href` and no router can sanitise it: `AuthPage.tsx:140` and
 * `AuthCallback.tsx:37`.
 */
function isSafeInternalPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  // Must run BEFORE the prefix tests — see CONTROL_OR_WHITESPACE.
  if (CONTROL_OR_WHITESPACE.test(value)) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//') || value.startsWith('/\\')) return false;
  if (value.startsWith('/auth') || value.startsWith('/login')) return false;
  return true;
}

/**
 * The stored shape since 2026-09-10. Anything else is a legacy bare string or
 * a tampered value, and both are cleared on read.
 */
interface StoredLastVisited {
  uid: string;
  path: string;
}

function isStoredShape(value: unknown): value is StoredLastVisited {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.uid === 'string' &&
    candidate.uid.length > 0 &&
    typeof candidate.path === 'string'
  );
}

/** Remove the stored value. Safe to call when nothing is stored. */
export function clearLastVisitedPage(): void {
  try {
    localStorage.removeItem(LAST_VISITED_PAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Record where THIS USER is, so a later sign-in by the same user can put them
 * back.
 *
 * No uid, no write. An identity-less write is precisely the defect this module
 * was re-shaped to remove: a caller with no authenticated user has nothing of
 * its own to record, and must not leave a path lying around for whoever signs
 * in next. `GroupInviteLanding` is the one surface that legitimately needs a
 * pre-auth return target, and it uses `src/lib/pending-invite.ts` instead.
 *
 * Silently no-ops on a value that would not survive `readLastVisitedPage()`
 * anyway — storing a path we would refuse to return is pure noise.
 */
export function writeLastVisitedPage(
  path: string,
  uid: string | null | undefined,
): void {
  if (!uid) return;
  if (!isSafeInternalPath(path)) return;
  try {
    localStorage.setItem(
      LAST_VISITED_PAGE_KEY,
      JSON.stringify({ uid, path } satisfies StoredLastVisited),
    );
  } catch {
    /* storage unavailable — the flow degrades to the domain default */
  }
}

/**
 * Read the path stored FOR THIS USER, or null when absent, unreadable,
 * invalid, or owned by somebody else.
 *
 * An invalid or foreign value is CLEARED on the way out — see the module
 * header. A MISSING uid returns null WITHOUT clearing: such a caller cannot
 * answer the ownership question, and destroying the legitimate owner's value
 * on their behalf would be strictly worse than declining to read it.
 *
 * Legacy bare-string values (the pre-2026-09-10 shape) fail `JSON.parse` — a
 * stored path always starts with `/`, which is never valid JSON — and are
 * cleared. The one-time migration cost is that everyone holding an old value
 * lands on `/dashboard` once, then starts accumulating a uid-keyed one.
 */
export function readLastVisitedPage(
  uid: string | null | undefined,
): string | null {
  if (!uid) return null;

  let raw: string | null;
  try {
    raw = localStorage.getItem(LAST_VISITED_PAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Legacy bare-string value, or garbage.
    clearLastVisitedPage();
    return null;
  }

  if (!isStoredShape(parsed) || !isSafeInternalPath(parsed.path)) {
    clearLastVisitedPage();
    return null;
  }

  if (parsed.uid !== uid) {
    // A different account used this browser. Clearing rather than ignoring is
    // what closes the shared-computer leak.
    clearLastVisitedPage();
    return null;
  }

  return parsed.path;
}
